import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  TimeOffEntry,
  TimeOffStatus,
  TimeOffType,
  useAddTimeOffEntryMutation,
  useUpdateTimeOffEntryMutation,
} from '../../../store/slices/timeOffSlice';
import { addNotification } from '../../../store/slices/uiSlice';
import { formatDateRange } from '../../../utils/format';
import { failureReason } from '../../../utils/requestErrors';
import { toApiDate } from './timeOffHours';

/** What the time-off form collects. Hours come from the dates (weekdays × 8). */
export interface TimeOffFormData {
  type: 'PTO' | 'SICK' | 'WFH';
  startDate: Date | null;
  endDate: Date | null;
  hours: number;
  notes: string;
}

/** The stored request an edit starts from, as GET /time-off/:id returns it. */
export type EditableTimeOff = Pick<TimeOffEntry, '_id' | 'type' | 'startDate' | 'endDate' | 'hours'> & {
  notes?: string | null;
  employee?: { name?: string | null } | null;
};

/** The API answers create and update with `{ timeOffEntry, conflicts }`, not the bare entry the slice types say. */
type SaveResult = { timeOffEntry?: Pick<TimeOffEntry, 'status' | 'type'> | null } | undefined;

/**
 * The status the API stored, which can differ from the one sent: it queues
 * short-notice PTO and WFH for in-office staff even when they were sent as
 * approved. Undefined when the response doesn't say.
 */
export const savedStatus = (result: unknown) => (result as SaveResult)?.timeOffEntry?.status;

const TYPE_PHRASE: Record<string, string> = {
  [TimeOffType.PTO]: 'PTO',
  [TimeOffType.SICK]: 'sick time',
  [TimeOffType.WFH]: 'WFH',
};

/** The type as it reads inside a sentence: "Added your sick time for Oct 8, 2026." */
const typePhrase = (type: string) => TYPE_PHRASE[type] ?? type;

const possessive = (name: string) => (name.endsWith('s') ? `${name}’` : `${name}’s`);

export const NO_EMPLOYEE_RECORD =
  'Your sign-in isn’t linked to an employee record, so there’s no one to file this for. Ask an admin to link it.';

/** A refusal shaped like an RTK Query 4xx, so the form explains it the same way as one from the API. */
const refusal = (reason: string) =>
  Object.assign(new Error(reason), { status: 400, data: { success: false, error: reason } });

/** POST /time-off body: the employee's id (not the populated object) and calendar dates. The API decides the status. */
export const buildCreateBody = (employeeId: string, form: TimeOffFormData) => ({
  employee: employeeId,
  type: form.type,
  startDate: toApiDate(form.startDate as Date),
  endDate: toApiDate(form.endDate as Date),
  hours: form.hours,
  notes: form.notes.trim() || undefined,
});

/**
 * Only what changed. The API treats every key it receives as an edit, and it
 * refuses some (a new type, new dates) on VP-reviewed requests, so a
 * notes-only edit must not resend the dates.
 */
export const buildEditChanges = (stored: EditableTimeOff, form: TimeOffFormData): Partial<TimeOffEntry> => {
  const changes: Partial<TimeOffEntry> = {};
  if (form.type !== stored.type) changes.type = form.type as TimeOffType;
  if (form.startDate) {
    const startDate = toApiDate(form.startDate);
    if (startDate !== stored.startDate) changes.startDate = startDate;
  }
  if (form.endDate) {
    const endDate = toApiDate(form.endDate);
    if (endDate !== stored.endDate) changes.endDate = endDate;
  }
  if (Number(form.hours) !== Number(stored.hours)) changes.hours = form.hours;
  const notes = form.notes.trim();
  if (notes !== (stored.notes || '').trim()) changes.notes = notes;
  return changes;
};

/** Sick time and eligible WFH are approved on arrival; everything else waits on a manager. */
export const createdMessage = (status: string | null | undefined, type: string, startDate: string, endDate: string) => {
  const dates = formatDateRange(startDate, endDate);
  return status === TimeOffStatus.APPROVED
    ? `Added your ${typePhrase(type)} for ${dates}. It’s approved.`
    : `Sent your ${typePhrase(type)} request for ${dates}. It’s waiting on approval.`;
};

export const savedMessage = (name: string | null | undefined, type: string) =>
  name?.trim()
    ? `Saved changes to ${possessive(name.trim())} ${typePhrase(type)} request.`
    : `Saved changes to this ${typePhrase(type)} request.`;

/**
 * What went wrong and what to do: the API's reason for a refusal ("Only HR or
 * an admin can override an active VP review."), otherwise a retry. The form
 * stays open, so it also says nothing typed was lost.
 */
export const saveErrorMessage = (err: unknown, isEdit: boolean) =>
  `${isEdit ? 'Couldn’t save these changes.' : 'Couldn’t send this request.'} ${failureReason(err)} Your entries are still here.`;

export interface SaveTimeOffOptions {
  isEdit: boolean;
  /** Edit: the request being changed. */
  timeOff?: EditableTimeOff | null;
  /** Create: the employee record the request is filed for. */
  employeeId?: string | null;
}

/** `unchanged`: an edit with nothing to send; the form just closes. */
export type SaveOutcome = 'created' | 'updated' | 'unchanged';

/**
 * Saves the time-off form: POST /time-off for a new request, PUT
 * /time-off/:id with only the changed fields for an edit. Both mutations
 * invalidate every TimeOff and Employee tag, so open lists, the request's own
 * page and balances refetch. Success raises a toast; a failure rejects with
 * the API error for the form to explain (see `saveErrorMessage`).
 *
 * The modal calls this itself, so nothing but plain data (`isEdit`, the
 * entry, an employee id) passes through the Redux modal state.
 */
export const useSaveTimeOffRequest = ({ isEdit, timeOff, employeeId }: SaveTimeOffOptions) => {
  const dispatch = useDispatch();
  const [addTimeOffEntry] = useAddTimeOffEntryMutation();
  const [updateTimeOffEntry] = useUpdateTimeOffEntryMutation();

  return useCallback(
    async (form: TimeOffFormData): Promise<SaveOutcome> => {
      if (!form.startDate || !form.endDate) throw refusal('Choose the first and last day off.');

      if (isEdit) {
        if (!timeOff?._id) throw refusal('This request didn’t load, so there’s nothing to save to. Reload the page and try again.');
        const changes = buildEditChanges(timeOff, form);
        if (Object.keys(changes).length === 0) return 'unchanged';
        const result = (await updateTimeOffEntry({ id: timeOff._id, timeOff: changes }).unwrap()) as unknown as SaveResult;
        dispatch(addNotification({
          type: 'success',
          message: savedMessage(timeOff.employee?.name, result?.timeOffEntry?.type ?? form.type),
        }));
        return 'updated';
      }

      if (!employeeId) throw refusal(NO_EMPLOYEE_RECORD);
      const body = buildCreateBody(employeeId, form);
      const result = (await addTimeOffEntry(body as unknown as Partial<TimeOffEntry>).unwrap()) as unknown as SaveResult;
      dispatch(addNotification({
        type: 'success',
        message: createdMessage(result?.timeOffEntry?.status, form.type, body.startDate, body.endDate),
      }));
      return 'created';
    },
    [addTimeOffEntry, dispatch, employeeId, isEdit, timeOff, updateTimeOffEntry]
  );
};
